import type { PayoutBatchesTranslations } from "@/shared/lang/interfaces/payoutBatches";

import {
  type ListPayoutBatchItemsParams,
  PayoutBatchItemStatus,
  PayoutBatchStatus,
} from "../types/payoutBatches.types";

export const LIST_PAGE_SIZE = 25;
export const ITEMS_PAGE_SIZE = 50;
export const EVENTS_PAGE_SIZE = 50;

/** Detail re-read cadence while the batch can still move on its own (validating, executing). */
export const DETAIL_POLL_MS = 5_000;
/** List refresh cadence while at least one visible batch is live. */
export const LIST_POLL_MS = 15_000;
/** Row toggles are coalesced into one PATCH per pause in clicking. */
export const SELECTION_DEBOUNCE_MS = 300;
/** The primary button of every money-moving dialog stays disabled while the summary is meant to be read. */
export const ARM_DELAY_MS = 1_500;

/** Mirrors coloca-nest `PAYOUT_BATCH_MAX_FILE_BYTES` (5 MiB, enforced by Multer). */
export const MAX_FILE_MB = 5;
/** Fallback when the summary carries no `drift_tolerance_pct` — the backend decides and publishes it. */
export const RATE_DRIFT_TOLERANCE_PCT = "1";
/** Mirrors coloca-nest `UploadPayoutBatchDto.concept` (`@MaxLength(120)`). */
export const CONCEPT_MAX_LENGTH = 120;
/** Mirrors coloca-nest `CancelPayoutBatchDto.reason` (`@MaxLength(255)`). */
export const CANCEL_REASON_MAX_LENGTH = 255;
/** Mirrors coloca-nest `RejectPayoutBatchDto.reason` (`@MinLength(3)`, `@MaxLength(255)`). */
export const REJECT_REASON_MIN_LENGTH = 3;
export const REJECT_REASON_MAX_LENGTH = 255;

export const LIVE_BATCH_STATUSES: ReadonlySet<PayoutBatchStatus> = new Set([
  PayoutBatchStatus.UPLOADED,
  PayoutBatchStatus.VALIDATING,
  PayoutBatchStatus.APPROVED,
  PayoutBatchStatus.EXECUTING,
]);

export const TERMINAL_BATCH_STATUSES: ReadonlySet<PayoutBatchStatus> = new Set([
  PayoutBatchStatus.COMPLETED,
  PayoutBatchStatus.COMPLETED_WITH_ERRORS,
  PayoutBatchStatus.FAILED,
  PayoutBatchStatus.CANCELLED,
  PayoutBatchStatus.EXPIRED,
]);

export const CANCELLABLE_BATCH_STATUSES: ReadonlySet<PayoutBatchStatus> = new Set([
  PayoutBatchStatus.UPLOADED,
  PayoutBatchStatus.VALIDATING,
  PayoutBatchStatus.VALIDATION_FAILED,
  PayoutBatchStatus.READY,
  PayoutBatchStatus.PENDING_APPROVAL,
  PayoutBatchStatus.APPROVED,
]);

/** The review surface: rows, balance and totals, selectable only while READY. */
export const REVIEW_BATCH_STATUSES: ReadonlySet<PayoutBatchStatus> = new Set([
  PayoutBatchStatus.READY,
  PayoutBatchStatus.PENDING_APPROVAL,
]);

/** Statuses the progress surface (execution and results) renders, as opposed to the review surface. */
export const EXECUTION_BATCH_STATUSES: ReadonlySet<PayoutBatchStatus> = new Set([
  PayoutBatchStatus.APPROVED,
  PayoutBatchStatus.EXECUTING,
  PayoutBatchStatus.COMPLETED,
  PayoutBatchStatus.COMPLETED_WITH_ERRORS,
  PayoutBatchStatus.FAILED,
]);

export type PillVariant = "success" | "warning" | "info" | "danger" | "neutral";

/** CANCELLED and EXPIRED are deliberate or expected closures, never painted as errors. */
export const BATCH_STATUS_PILL: Record<PayoutBatchStatus, PillVariant> = {
  [PayoutBatchStatus.UPLOADED]: "neutral",
  [PayoutBatchStatus.VALIDATING]: "info",
  [PayoutBatchStatus.VALIDATION_FAILED]: "danger",
  [PayoutBatchStatus.READY]: "neutral",
  [PayoutBatchStatus.PENDING_APPROVAL]: "warning",
  [PayoutBatchStatus.APPROVED]: "info",
  [PayoutBatchStatus.EXECUTING]: "info",
  [PayoutBatchStatus.COMPLETED]: "success",
  [PayoutBatchStatus.COMPLETED_WITH_ERRORS]: "warning",
  [PayoutBatchStatus.FAILED]: "danger",
  [PayoutBatchStatus.CANCELLED]: "neutral",
  [PayoutBatchStatus.EXPIRED]: "neutral",
};

export const ITEM_STATUS_PILL: Record<PayoutBatchItemStatus, PillVariant> = {
  [PayoutBatchItemStatus.PENDING]: "neutral",
  [PayoutBatchItemStatus.INVALID]: "danger",
  [PayoutBatchItemStatus.EXCLUDED]: "neutral",
  [PayoutBatchItemStatus.SELECTED]: "info",
  [PayoutBatchItemStatus.QUEUED]: "info",
  [PayoutBatchItemStatus.PROCESSING]: "info",
  [PayoutBatchItemStatus.SUBMITTED]: "warning",
  [PayoutBatchItemStatus.PAID]: "success",
  [PayoutBatchItemStatus.FAILED]: "danger",
  [PayoutBatchItemStatus.REVERSED]: "danger",
  [PayoutBatchItemStatus.CANCELLED]: "neutral",
};

/** Rows that are still moving — the pill shows a pulsing dot for them. */
export const MOVING_ITEM_STATUSES: ReadonlySet<PayoutBatchItemStatus> = new Set([
  PayoutBatchItemStatus.QUEUED,
  PayoutBatchItemStatus.PROCESSING,
  PayoutBatchItemStatus.SUBMITTED,
]);

export const BATCH_STATUS_LABEL_KEYS: Record<PayoutBatchStatus, keyof PayoutBatchesTranslations> = {
  [PayoutBatchStatus.UPLOADED]: "status_batch_uploaded",
  [PayoutBatchStatus.VALIDATING]: "status_batch_validating",
  [PayoutBatchStatus.VALIDATION_FAILED]: "status_batch_validation_failed",
  [PayoutBatchStatus.READY]: "status_batch_ready",
  [PayoutBatchStatus.PENDING_APPROVAL]: "status_batch_pending_approval",
  [PayoutBatchStatus.APPROVED]: "status_batch_approved",
  [PayoutBatchStatus.EXECUTING]: "status_batch_executing",
  [PayoutBatchStatus.COMPLETED]: "status_batch_completed",
  [PayoutBatchStatus.COMPLETED_WITH_ERRORS]: "status_batch_completed_with_errors",
  [PayoutBatchStatus.FAILED]: "status_batch_failed",
  [PayoutBatchStatus.CANCELLED]: "status_batch_cancelled",
  [PayoutBatchStatus.EXPIRED]: "status_batch_expired",
};

export const ITEM_STATUS_LABEL_KEYS: Record<PayoutBatchItemStatus, keyof PayoutBatchesTranslations> = {
  [PayoutBatchItemStatus.PENDING]: "status_item_pending",
  [PayoutBatchItemStatus.INVALID]: "status_item_invalid",
  [PayoutBatchItemStatus.EXCLUDED]: "status_item_excluded",
  [PayoutBatchItemStatus.SELECTED]: "status_item_selected",
  [PayoutBatchItemStatus.QUEUED]: "status_item_queued",
  [PayoutBatchItemStatus.PROCESSING]: "status_item_processing",
  [PayoutBatchItemStatus.SUBMITTED]: "status_item_submitted",
  [PayoutBatchItemStatus.PAID]: "status_item_paid",
  [PayoutBatchItemStatus.FAILED]: "status_item_failed",
  [PayoutBatchItemStatus.REVERSED]: "status_item_reversed",
  [PayoutBatchItemStatus.CANCELLED]: "status_item_cancelled",
};

/** Event type → label key. An unknown new type falls back to the raw enum at the call site. */
export const EVENT_TYPE_LABEL_KEYS: Partial<Record<string, keyof PayoutBatchesTranslations>> = {
  BATCH_UPLOADED: "event_type_batch_uploaded",
  FILE_ARCHIVED: "event_type_file_archived",
  VALIDATION_STARTED: "event_type_validation_started",
  VALIDATION_FINISHED: "event_type_validation_finished",
  VALIDATION_FAILED: "event_type_validation_failed",
  ITEM_EXCLUDED: "event_type_item_excluded",
  ITEM_INCLUDED: "event_type_item_included",
  SELECTION_CHANGED: "event_type_selection_changed",
  REVALIDATION_REQUESTED: "event_type_revalidation_requested",
  SUBMITTED_FOR_APPROVAL: "event_type_submitted_for_approval",
  APPROVAL_REJECTED: "event_type_approval_rejected",
  SUBMISSION_WITHDRAWN: "event_type_submission_withdrawn",
  APPROVED: "event_type_approved",
  PROVIDER_BALANCE_ACKNOWLEDGED: "event_type_provider_balance_acknowledged",
  EXECUTION_ENQUEUED: "event_type_execution_enqueued",
  EXECUTION_ENQUEUE_FAILED: "event_type_execution_enqueue_failed",
  EXECUTION_STARTED: "event_type_execution_started",
  ITEM_PROCESSING: "event_type_item_processing",
  ITEM_SUBMITTED: "event_type_item_submitted",
  ITEM_PAID: "event_type_item_paid",
  ITEM_FAILED: "event_type_item_failed",
  ITEM_REVERSED: "event_type_item_reversed",
  ITEM_STATUS_SYNCED: "event_type_item_status_synced",
  BATCH_HALTED: "event_type_batch_halted",
  EXECUTION_FINISHED: "event_type_execution_finished",
  EXECUTION_FAILED: "event_type_execution_failed",
  RETRY_REQUESTED: "event_type_retry_requested",
  RUN_RESUMED: "event_type_run_resumed",
  CANCELLED: "event_type_cancelled",
  EXPIRED: "event_type_expired",
  STALL_RESUMED: "event_type_stall_resumed",
  ALERT_SENT: "event_type_alert_sent",
};

/** Event types that mark a failure — drawn with the danger tone in the timeline. */
export const FAILURE_EVENT_TYPES: ReadonlySet<string> = new Set([
  "VALIDATION_FAILED",
  "EXECUTION_ENQUEUE_FAILED",
  "ITEM_FAILED",
  "ITEM_REVERSED",
  "BATCH_HALTED",
  "EXECUTION_FAILED",
]);

export type ListKpiFilterId =
  | "all"
  | "pending_review"
  | "pending_approval"
  | "executing"
  | "completed"
  | "with_errors"
  | "closed";

export interface ListKpiFilter {
  id: ListKpiFilterId;
  labelKey: keyof PayoutBatchesTranslations;
  /** `null` = no status filter. Several statuses = one request per status, merged client-side. */
  statuses: readonly PayoutBatchStatus[] | null;
}

export const LIST_KPI_FILTERS: readonly ListKpiFilter[] = [
  { id: "all", labelKey: "kpi_all", statuses: null },
  { id: "pending_review", labelKey: "kpi_pending_review", statuses: [PayoutBatchStatus.READY] },
  {
    id: "pending_approval",
    labelKey: "kpi_pending_approval",
    statuses: [PayoutBatchStatus.PENDING_APPROVAL],
  },
  {
    id: "executing",
    labelKey: "kpi_executing",
    statuses: [PayoutBatchStatus.APPROVED, PayoutBatchStatus.EXECUTING],
  },
  { id: "completed", labelKey: "kpi_completed", statuses: [PayoutBatchStatus.COMPLETED] },
  {
    id: "with_errors",
    labelKey: "kpi_with_errors",
    statuses: [PayoutBatchStatus.COMPLETED_WITH_ERRORS, PayoutBatchStatus.FAILED],
  },
  {
    id: "closed",
    labelKey: "kpi_closed",
    statuses: [PayoutBatchStatus.CANCELLED, PayoutBatchStatus.EXPIRED],
  },
];

export type ItemsFilterId =
  | "all"
  | "selected"
  | "errors"
  | "warnings"
  | "excluded"
  | "processing"
  | "submitted"
  | "paid"
  | "failed"
  | "reversed";

export interface ItemsFilter {
  id: ItemsFilterId;
  labelKey: keyof PayoutBatchesTranslations;
  query: Pick<ListPayoutBatchItemsParams, "status" | "hasErrors" | "hasWarnings">;
}

/** Review surface: what is in, what is out, what needs a look. */
export const REVIEW_ITEMS_FILTERS: readonly ItemsFilter[] = [
  { id: "all", labelKey: "items_filter_all", query: {} },
  { id: "selected", labelKey: "items_filter_selected", query: { status: PayoutBatchItemStatus.SELECTED } },
  { id: "errors", labelKey: "items_filter_errors", query: { hasErrors: true } },
  { id: "warnings", labelKey: "items_filter_warnings", query: { hasWarnings: true } },
  { id: "excluded", labelKey: "items_filter_excluded", query: { status: PayoutBatchItemStatus.EXCLUDED } },
];

/** Progress surface: outcomes. */
export const EXECUTION_ITEMS_FILTERS: readonly ItemsFilter[] = [
  { id: "all", labelKey: "items_filter_all", query: {} },
  {
    id: "processing",
    labelKey: "items_filter_processing",
    query: { status: PayoutBatchItemStatus.PROCESSING },
  },
  { id: "submitted", labelKey: "items_filter_submitted", query: { status: PayoutBatchItemStatus.SUBMITTED } },
  { id: "paid", labelKey: "items_filter_paid", query: { status: PayoutBatchItemStatus.PAID } },
  { id: "failed", labelKey: "items_filter_failed", query: { status: PayoutBatchItemStatus.FAILED } },
  { id: "reversed", labelKey: "items_filter_reversed", query: { status: PayoutBatchItemStatus.REVERSED } },
];

export const SUGGESTION_LABEL_KEYS: Record<string, keyof PayoutBatchesTranslations> = {
  keep_file_order: "suggestion_keep_file_order",
  smallest_first: "suggestion_smallest_first",
  largest_first: "suggestion_largest_first",
};

export const LIQUIDITY_STATUS_LABEL_KEYS: Record<string, keyof PayoutBatchesTranslations> = {
  OK: "liquidity_ok",
  STALE: "liquidity_stale",
  UNAVAILABLE: "liquidity_unavailable",
  UNSUPPORTED: "liquidity_unsupported",
};

/**
 * 409 / 400 `code`s the batch endpoints answer with, mapped to copy of our own. The backend sentence is
 * localized too and stays the fallback for any code missing here.
 */
export const BATCH_ERROR_CODE_LABEL_KEYS: Partial<Record<string, keyof PayoutBatchesTranslations>> = {
  fingerprint_mismatch: "error_fingerprint_mismatch",
  not_ready: "error_not_ready",
  nothing_selected: "error_nothing_selected",
  insufficient_funds: "error_insufficient_funds",
  organization_busy: "error_organization_busy",
  provider_balance_ack_required: "error_provider_balance_ack_required",
  expired: "error_expired",
  retry_debit_exceeded: "error_retry_debit_exceeded",
  items_not_retryable: "error_items_not_retryable",
  invalid_status: "error_invalid_status",
  wallet_frozen: "error_wallet_frozen",
  rail_disabled: "error_rail_disabled",
  organization_verification_required: "error_verification_required",
  fees_changed: "error_fees_changed",
  self_approval_forbidden: "error_self_approval_forbidden",
  not_pending_approval: "error_not_pending_approval",
  payout_batches_not_enabled: "error_not_enabled",
};

/** System-written `reason`s of trail events; anything else (a cancel reason someone typed) renders as it came. */
export const EVENT_REASON_LABEL_KEYS: Partial<Record<string, keyof PayoutBatchesTranslations>> = {
  fees_changed: "event_reason_fees_changed",
};
